// "Sauna ready" chime — either a recorded sample (default) or a synthesised two-note bell.
// Both paths swallow autoplay-blocked errors quietly; browsers may suppress the first
// playback after page load if there's been no user gesture yet.

import saunaSampleUrl from '@/assets/384180__xiiisamples__sauna.wav';

import type { SaunaChimeSound } from './config';

let ctx: AudioContext | null = null;
let sampleAudio: HTMLAudioElement | null = null;

function getContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  if (!ctx) ctx = new Ctor();
  return ctx;
}

function playTone(audio: AudioContext, frequency: number, startAt: number, duration = 0.9): void {
  const osc = audio.createOscillator();
  const gain = audio.createGain();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(frequency, startAt);
  gain.gain.setValueAtTime(0.0001, startAt);
  gain.gain.exponentialRampToValueAtTime(0.35, startAt + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.0001, startAt + duration);
  osc.connect(gain).connect(audio.destination);
  osc.start(startAt);
  osc.stop(startAt + duration + 0.05);
}

function playSynthesisedTone(): void {
  const audio = getContext();
  if (!audio) return;
  if (audio.state === 'suspended') void audio.resume().catch(() => undefined);
  const t0 = audio.currentTime + 0.02;
  // Major sixth: C5 → A5 — bright, pleasant, attention-grabbing without harshness.
  playTone(audio, 523.25, t0, 0.9);
  playTone(audio, 880.0, t0 + 0.18, 1.1);
}

function playSaunaSample(): void {
  if (typeof window === 'undefined') return;
  if (!sampleAudio) {
    sampleAudio = new Audio(saunaSampleUrl);
    sampleAudio.preload = 'auto';
  }
  try {
    sampleAudio.currentTime = 0;
  } catch {
    // Some browsers throw if not yet loaded — ignore.
  }
  void sampleAudio.play().catch(() => undefined);
}

export function playReadyChime(sound: SaunaChimeSound = 'sample'): void {
  if (sound === 'tone') {
    playSynthesisedTone();
    return;
  }
  playSaunaSample();
}
